/*
 * Models for the learner, including the search model and the learner results, for both clustered
 * and non-clustered transcripts.  Ideally, we would have two model files, one for the search model
 * in LearnerConfig and one for the results in LearnerResults.  However, both really depend on each
 * other so I am putting them in one file at a higher level.
 */

define([
  "backbone",
  "jquery",
  "underscore",
  "moment",
  "configuration/properties",
  "messaging/messenger",
  "modules/shared/sortableList/sortableListModel",
  "modules/learner/learnerTranscriptModal/conversationTranscript/conversationTranscriptModel",
], function (
  Backbone,
  $,
  _,
  Moment,
  Properties,
  Messenger,
  SortableListModel,
  ConversationTranscriptModel,
) {
  "use strict";

  const LearnerModel = {};

  const CLUSTERING_JOB_STATUS = Object.freeze({
    AWAITING: "AWAITING",
    SCHEDULED: "SCHEDULED",
    ENQUEUED: "ENQUEUED",
    FAILED: "FAILED",
    PROCESSING: "PROCESSING",
    SUCCEEDED: "SUCCEEDED",
    DELETED: "DELETED",
  });

  LearnerModel.learnerSearch = Backbone.Model.extend({
    /* The clustering slider selects the clustering threshold on a scale of 0 to 100.  This is a
     * multiple of 100 of the range of the previous threshold, which was from 0 to 1.0.
     * HOWEVER, the slider does not span the entire range of values.  The very high and very low ranges
     * are not valuable, so instead we crop the range to 10-90, with increments of 20 (10, 30, 50, 70, 90).
     * This limits the possible thresholds and also eliminates thresholds which should not be used.
     */
    defaults: {
      daysBack: 30,
      timeIncrement: 60,
      endDateMilliOffset: 0, //millisecond offset of the end date used to set the hour field
    },
    initializeCustom: function () {
      //initialize the time dropdown to be properly i18n'ed
      //loop through the minutes of the day in timeIncrement minute increments
      const timeOptions = [];
      const minutesPerDay = 1440;
      const midnight = Moment({ minute: 0 });
      for (var minutes = 0; minutes < minutesPerDay; minutes += this.get("timeIncrement")) {
        const time = Moment({ minute: 0 }).add(minutes, "minutes");
        const timeDisplay = Messenger.reqres.request("get:formatted:time", time);
        //time value is the millisecond offset from midnight to the timeString
        timeOptions.push({
          timeValue: time.valueOf() - midnight.valueOf(),
          timeString: timeDisplay,
        });
      }
      this.set("timeOptions", timeOptions);
      //set the endDate offset to be the start of the next hour (so if it's 16:45, it will set it to 17:00)
      const nextHour = Moment().add(1, "hours").minutes(0).seconds(0).milliseconds(0);
      //get the milliseconds offset from midnight
      this.set("endDateMilliOffset", nextHour.valueOf() - midnight.valueOf());
    },
    //hash converts slider position to clustering threshold
    thresholdConverter: {
      1: 70,
      2: 50,
      3: 35,
      4: 20,
      5: 10,
    },
    semanticThresholdConverter: {
      1: 70,
      2: 50,
      3: 35,
      4: 30,
      5: 25,
      6: 20,
      7: 15,
      8: 10,
      9: 5,
    },
    semanticThresholdToSliderValueConverter: {
      70: 1,
      50: 2,
      35: 3,
      30: 4,
      25: 5,
      20: 6,
      15: 7,
      10: 8,
      5: 9,
    },
    //converts the slider value (from 1 to 5, where 1 is loosely clustered, and 5 is tightly clustered) to a
    //threshold value (0 to 100, where 0 is tightly clustered and 100 is loosely clustered)
    getThreshold: function (sliderValue) {
      return this.thresholdConverter[sliderValue];
    },
    //converts the slider value (from 1 to 9, where 1 is loosely clustered, and 9 is tightly clustered) to a
    //threshold value 0 top 100
    getSemanticThreshold: function (sliderValue) {
      return this.semanticThresholdConverter[sliderValue];
    },
    getSemanticClusterSliderValueFromThreshold: function (threshold) {
      return this.semanticThresholdToSliderValueConverter[threshold];
    },
  });

  LearnerModel.martTranscriptCount = Backbone.Model.extend({
    initializeCustom: function (parameters) {
      this.parameters = parameters;
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      const parameters = this.parameters;
      return `${Properties.rootURL}/${kbId}/mart/transcripts/touchpoint/${parameters.touchpoint}/start/${parameters.startDate}/end/${parameters.endDate}/match_status/${parameters.matchType}/auth_status/${parameters.authoringStatus}/locale/${parameters.localeId}/count`;
    },
  });

  //Model for transcripts obtained from the mart (unclustered)
  LearnerModel.martTranscript = Backbone.Model.extend({
    idAttribute: "utteranceId",
    agentAttributeCodes: ["B01", "B02", "C01"],
    caseAttributeCode: "BF1",
    //transcript has been learned, so mark it as completed and save the changes to the server
    complete: function () {
      //if the transcript is part of a cluster, call complete() on the representative cluster
      if (this.clusterRepresentative) {
        this.clusterRepresentative.complete();
        this.trigger("remove"); //trigger a remove because the cluster will be removed from whatever collection it is in, so this transcript will be as well.
        return;
      }
      //get completed status id
      var statusCol = Messenger.reqres.request("kb:mart:statuses");
      var completedStatus = statusCol.findWhere({ value: "Completed" }); //should we remove the hardcoded string?
      if (!completedStatus) {
        console.log("'Completed' status could not be found");
      } else {
        var completedStatusId = completedStatus.get("id");
        this.updateStatus(completedStatusId);
      }
    },
    //override default function to delete the transcript
    deleteModel: function () {
      //if the transcript is part of a cluster, call deleteModel() on the representative cluster
      if (this.clusterRepresentative) {
        this.clusterRepresentative.deleteModel();
        this.trigger("remove"); //trigger a remove because the cluster will be removed from whatever collection it is in, so this transcript will be as well.
        return;
      }
      //deleting the model simply involves changing the status to null
      this.updateStatus(null);
    },
    getConversationTranscript: function () {
      if (!this.conversationTranscript) {
        var kbId = Messenger.reqres.request("active:kb");
        var url = Properties.rootURL + "/" + kbId + "/mart/conversation/" + this.get("sessionId");
        this.conversationTranscript = new ConversationTranscriptModel.conversationTranscript();
        this.conversationTranscript.url = url;
        this.conversationTranscript.fetch();
      }
      return this.conversationTranscript;
    },
    parse: function (response) {
      //replace a blank utterance string with <blank>
      response.userUtterance = response.userUtterance.trim();
      if (response.userUtterance.length === 0) {
        response.userUtterance = "[" + $.t("learner.blankUtterance") + "]";
      }
      //add topicMatchNames which lists the names of topic matches for the representative transcript of the cluster
      const topicMatchNames = [];
      for (let i = 0; i < response.topicMatches.length; i++) {
        const topic = response.topicMatches[i];
        const label = topic.topicId > 0 ? topic.topicName : $.t("common.none");
        topicMatchNames.push(label);
      }

      response.topicMatchNames = topicMatchNames;
      this._calculateAgentAssistFields(response);

      //add namespaced conversation attributes to the response hash for all data attributes so that it can easily be read by the sortable list
      if (this.collection.conversationAttributes) {
        //for each conversation attribute (which we collected when parsing the collection), find if this attribute has a value
        //otherwise, set it to empty string
        _.each(this.collection.conversationAttributes, function (attrHashName, attrName) {
          var attribute = _.findWhere(response.attributes, { name: attrName });
          response[attrHashName] = attribute ? attribute.value : "";
        });
      }

      return response;
    },
    //updates the statusId for this transcript and save the change to the server
    updateStatus: function (statusId) {
      this.set("learnerStatus", statusId);
      this.save(null, {
        success: function () {
          //remove the transcript from the list, since it is now a different status
          if (this.collection) {
            this.collection.remove(this);
          }
        },
        error: function () {
          Messenger.saveAttempted(false);
        },
      });
    },
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/mart/transcripts/" + this.get("utteranceId");
    },
    _calculateAgentAssistFields: function (response) {
      //extract the agent name and/or case ID from the attributes, if they exist
      response.agentName = null;
      response.caseId = null;
      if (response.attributes) {
        _.each(this.agentAttributeCodes, function (code) {
          var attribute = _.findWhere(response.attributes, { name: code });
          if (attribute) {
            response.agentName = attribute.value;
          }
        });
        var attribute = _.findWhere(response.attributes, { name: this.caseAttributeCode });
        if (attribute) {
          response.caseId = attribute.value;
        }
      }
    },
  });

  LearnerModel.learnerResults = Backbone.Collection.extend({
    model: LearnerModel.martTranscript,
    //delete all transcripts in the passed array
    deleteModels: function (transcriptArray) {
      this.updateStatuses(null, transcriptArray);
    },
    parse: function (response) {
      //when we receive the list of models from the server, we should create a hash (attribute title: attribute name in the model hash)
      //of all the conversation attributes in the results
      //we can then use that list when parsing individual transcripts (making sure the attribute exists for all transcripts)
      //as well as setting up the header collection.
      this.conversationAttributes = {};
      _.each(
        response,
        function (transcript) {
          if (transcript.attributes) {
            _.each(
              transcript.attributes,
              function (attribute) {
                //Make sure to replace any whitespace in the attribute name with underscores. No whitespace in variable names!
                this.conversationAttributes[attribute.name] =
                  "_convAttr" + attribute.name.replace(/ /g, "_"); //create a "namespaced" attribute for the transcript model starting with _convAttr
              },
              this,
            );
          }
        },
        this,
      );
      return response;
    },
    //updates the statuses of transcripts with an model in transcriptIdArray and save the changes to the server
    updateStatuses: function (statusId, transcriptArray) {
      //create a multi transcript status model to save the status changes
      var updateModel = new LearnerModel.multiTranscriptStatusModel({
        statusId: statusId,
        utteranceIds: _.map(transcriptArray, function (transcript) {
          return transcript.get("utteranceId");
        }),
      });
      var _collection = this;
      updateModel.save(null, {
        success: function () {
          //remove the transcripts from the list, since it is now a different status
          _collection.remove(transcriptArray, { silent: true }); //silence to avoid processing the remove of each model from the sortableList
          _collection.trigger("removeMultiple", transcriptArray); //trigger remove event to just have the collection view deal with the removal after everything has be done
          _.each(transcriptArray, function (transcript) {
            transcript.trigger("remove");
          });
        },
        error: function () {
          Messenger.saveAttempted(false);
        },
      });
      return updateModel;
    },
  });

  //Model for transcript clusters obtained from the mart
  LearnerModel.martClusteroid = Backbone.Model.extend({
    isCluster: true, //used to differentiate a martClusteroid from a martTranscript
    //cluster has been learned, so mark it as completed and save the changes to the server
    complete: function () {
      //get completed status id
      var statusCol = Messenger.reqres.request("kb:mart:statuses");
      var completedStatus = statusCol.findWhere({ value: "Completed" }); //should we remove the hardcoded string?
      if (!completedStatus) {
        console.log("'Completed' status could not be found");
      } else {
        var completedStatusId = completedStatus.get("id");
        this.updateStatus(completedStatusId);
      }
    },
    //override default function to delete the transcripts in the cluster
    deleteModel: function () {
      this.updateStatus(null);
    },
    getChildren: function () {
      //get collection of child martTranscripts
      return this.childrenCol;
    },
    /*
     * Modify the incoming martClusteroid model to better correspond to the martTranscript model.
     * The attributes hash is stripped of its children and utterance attributes.  The method
     * returns the transcript attribute (which is what martTranscript uses), along with a count
     * of the number of children.  In addition, a collection of child martTranscripts is added to
     * the model object (outside of the Backbone attributes)
     */
    parse: function (response) {
      //parse the children of the clusteroid as a collection of mart transcript models
      this.childrenCol = new LearnerModel.clusterChildren();
      _.each(
        response.children,
        function (child) {
          const childTranscript = new LearnerModel.martTranscript(child.representative);
          //add attribute to store transcript Ids of this representative child transcript
          childTranscript.set("utteranceIds", child.utteranceIds);
          const childUtterance = childTranscript.get("userUtterance").trim();
          let childTranslatedUtterance;
          if (childTranscript.get("translatedUtterance")) {
            childTranslatedUtterance = childTranscript.get("translatedUtterance").trim();
          }
          //define a name attribute for use by the selectable list (should be the utterance followed by the utterance count)
          childTranscript.set("name", childUtterance + " (" + child.utteranceIds.length + ")");
          childTranscript.set("utterance", childUtterance);
          //if the child transcript is the main cluster transcript, set the
          // cluster's user utterance, learner status, and topic matches
          if (response.utteranceId === childTranscript.get("utteranceId")) {
            //replace a blank utterance string with <blank>
            if (childUtterance.length > 0) {
              response.userUtterance = childUtterance;
            } else {
              response.userUtterance = "[" + $.t("learner.blankUtterance") + "]";
            }
            if (childTranslatedUtterance) {
              response.translatedUtterance = childTranslatedUtterance;
            } else {
              response.translatedUtterance = "[" + $.t("learner.blankUtterance") + "]";
            }
            //add topicMatches which lists the matches for the representative transcript of the cluster
            const topicMatchNames = [];
            for (let i = 0; i < childTranscript.get("topicMatches").length; i++) {
              const topic = childTranscript.get("topicMatches")[i];
              const label = topic.topicId > 0 ? topic.topicName : $.t("common.none");
              topicMatchNames.push(label);
            }
            response.topicMatchNames = topicMatchNames;
            response.learnerStatus = childTranscript.get("learnerStatus");
          }
          //add a reference to the representative transcript (also marks the child transcript as part of a cluster)
          childTranscript.clusterRepresentative = this;
          this.childrenCol.add(childTranscript);
        },
        this,
      );

      return response;
    },
    //updates the statusId for each transcript in the cluster and saves the changes to the server
    updateStatus: function (statusId) {
      //create a multi transcript status model to save the status changes
      //gather transcript Ids to update by getting the ids from the children
      var utteranceIds = [];
      this.childrenCol.each(function (child) {
        var childUtteranceIds = child.get("utteranceIds");
        //merge transcriptIds into single array
        utteranceIds = _.union(utteranceIds, childUtteranceIds);
      });
      var updateModel = new LearnerModel.multiTranscriptStatusModel({
        statusId: statusId,
        utteranceIds: utteranceIds,
      });

      var _model = this;
      updateModel.save(null, {
        success: function () {
          //remove the transcript from the list, since it is now a different status
          if (_model.collection) {
            _model.collection.remove(_model);
          }
        },
        error: function () {
          Messenger.saveAttempted(false);
        },
      });
    },
  });

  LearnerModel.clusteringJob = Backbone.Model.extend({
    idAttribute: "jobId",
    defaults: {
      analyticsTags: null,
      authoringStatus: "",
      clusterThreshold: 0,
      endDate: 0,
      jobScheduledTimestamp: null,
      jobStatus: null,
      localeIds: [],
      matchingStatus: 0,
      startDate: 0,
      touchpointIds: [],
    },
    deleteAllJobsForUser: function () {
      // set dummy ID so that we can call deleteModel() on the model.
      let job = new Backbone.Model({ id: "dummyId" });
      const kbId = Messenger.reqres.request("active:kb");
      const modelOptions = {
        url: `${Properties.rootURL}/${kbId}/mart/transcripts/clustering/jobs`,
      };
      job.deleteModel(modelOptions);
      return job;
    },
    setAttributesFromLearnerConfigData: function (learnerConfigData) {
      learnerConfigData = learnerConfigData || {};
      this.set({
        matchingStatus: +learnerConfigData.matchType,
        authoringStatus: learnerConfigData.authoringStatus,
        touchpointIds: learnerConfigData.touchpoint.split("_").map(Number),
        localeIds: [parseInt(learnerConfigData.localeId, 10)],
        startDate: learnerConfigData.startDate,
        endDate: learnerConfigData.endDate,
        clusterThreshold: learnerConfigData.threshold,
      });
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      const jobId = this.get("jobId");
      if (!jobId) {
        return `${Properties.rootURL}/${kbId}/mart/transcripts/clustering/job`;
      } else {
        return `${Properties.rootURL}/${kbId}/mart/transcripts/clustering/job/${jobId}`;
      }
    },
    isClusteringJobSuccess: function () {
      return this.get("jobStatus") === CLUSTERING_JOB_STATUS.SUCCEEDED;
    },
    isClusteringJobFailed: function () {
      const jobStatus = this.get("jobStatus");
      return (
        jobStatus === CLUSTERING_JOB_STATUS.FAILED || jobStatus === CLUSTERING_JOB_STATUS.DELETED
      );
    },
    isClusteringJobPending: function () {
      const jobStatus = this.get("jobStatus");
      return (
        jobStatus === CLUSTERING_JOB_STATUS.AWAITING ||
        jobStatus === CLUSTERING_JOB_STATUS.SCHEDULED ||
        jobStatus === CLUSTERING_JOB_STATUS.ENQUEUED ||
        jobStatus === CLUSTERING_JOB_STATUS.PROCESSING
      );
    },
  });

  LearnerModel.clusteredLearnerResults = Backbone.Collection.extend({
    model: LearnerModel.martClusteroid,
    //delete all clusters (and therefore, all the transcripts) in the passed array
    deleteModels: function (clusterArray) {
      return this.updateStatuses(null, clusterArray);
    },
    //updates the statuses of clusters with an model in ClusterArray and save the changes to the server
    updateStatuses: function (statusId, clusterArray) {
      //gather transcript Ids to update by getting the ids from the children
      var utteranceIds = [];
      _.each(clusterArray, function (cluster) {
        //get all transcript ids in the cluster
        cluster.childrenCol.each(function (child) {
          var childUtteranceIds = child.get("utteranceIds");
          //merge transcriptIds into single array
          utteranceIds = _.union(utteranceIds, childUtteranceIds);
        });
      });
      //create a multi transcript status model to save the status changes
      var updateModel = new LearnerModel.multiTranscriptStatusModel({
        statusId: statusId,
        utteranceIds: utteranceIds,
      });
      var _collection = this;
      updateModel.save(null, {
        success: function () {
          //remove the clusters from the list, since it is now a different status
          _collection.remove(clusterArray, { silent: true }); //silence to avoid processing the remove of each model from the sortableList
          _collection.trigger("removeMultiple", clusterArray); //trigger special remove event to just have the collection view deal with the removal after everything has be done
          _.each(clusterArray, function (cluster) {
            cluster.trigger("remove");
          });
        },
        error: function () {
          Messenger.saveAttempted(false);
        },
      });
      return updateModel;
    },
  });

  //Collection for child transcripts of a mart clusteroid
  LearnerModel.clusterChildren = Backbone.Collection.extend({
    model: LearnerModel.martTranscript,
    comparator: function (child1, child2) {
      //number of utterances represented by this child transcript
      var child1Count = child1.get("utteranceIds").length;
      var child2Count = child2.get("utteranceIds").length;
      //primary sort by descending utterance count, secondary sort by ascending utterance
      if (child1Count > child2Count) {
        return -1;
      }
      if (child1Count < child2Count) {
        return 1;
      }
      //count is equal, so compare utterances using the default text comparator
      return Backbone.textComparator(child1, child2, "userUtterance");
    },
  });

  //Model for handling multi-transcript status changes
  LearnerModel.multiTranscriptStatusModel = Backbone.Model.extend({
    idAttribute: "utteranceIds",
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/mart/transcripts/status";
    },
  });

  /*
   * Return a default learner search model with the starting search parameters
   */
  Messenger.reqres.setHandler(
    "learner:search:model",
    function () {
      return new LearnerModel.learnerSearch({
        touchpoints: Messenger.reqres.request("kb:touchpoints"),
        locales: Messenger.reqres.request("kb:languages"),
        authoringStatuses: Messenger.reqres.request("kb:mart:statuses"),
        defaultTouchpoint: Messenger.reqres.request("default:touchpoint"), //select default tp
      });
    },
    this,
  );

  /*
   * Return the learner results for the passed data object, which contains a hash
   * of all the search parameters
   */
  Messenger.reqres.setHandler(
    "learner:search:results",
    function (parameters) {
      const result = new LearnerModel.learnerResults();
      //only fetch the results if the start date <= end date
      if (parameters.startDate <= parameters.endDate) {
        const kbId = Messenger.reqres.request("active:kb");
        //construct fetch url from the parameter object
        result.url = function () {
          return `${Properties.rootURL}/${kbId}/mart/transcripts/touchpoint/${parameters.touchpoint}/start/${parameters.startDate}/end/${parameters.endDate}/match_status/${parameters.matchType}/auth_status/${parameters.authoringStatus}/locale/${parameters.localeId}`;
        };
        result.fetch();
      }
      return result;
    },
    this,
  );

  /**
   * Returns a count of all the emerging topics that would be returned with the given search
   * parameters. Used by the main dashboard.
   */
  Messenger.reqres.setHandler(
    "learner:emerging:count",
    function (parameters) {
      var count = new Backbone.Model({
        value: -1,
        threshold: parameters.size,
      });
      var kbId = Messenger.reqres.request("active:kb");
      //construct fetch url from the parameter object
      count.url = function () {
        return (
          Properties.rootURL +
          "/" +
          kbId +
          "/mart/transcripts/emerging/touchpoint/" +
          parameters.touchpoint +
          "/start/" +
          parameters.startDate +
          "/end/" +
          parameters.endDate +
          "/minimum/" +
          parameters.size +
          "/localeId/" +
          parameters.localeId
        );
      };
      count.fetch();
      return count;
    },
    this,
  );

  /*
   * Return the *clustered* learner results for the passed data object, which contains a hash
   * of all the search parameters
   */
  Messenger.reqres.setHandler(
    "learner:clustered:search:results",
    function (parameters) {
      const result = new LearnerModel.clusteredLearnerResults();
      //only fetch if the start date <= end date
      if (parameters.startDate <= parameters.endDate) {
        const kbId = Messenger.reqres.request("active:kb");
        //construct fetch url from the parameter object
        result.url = function () {
          const queryParams = parameters.jobId
            ? `?jobId=${encodeURIComponent(parameters.jobId)}`
            : "";
          return `${Properties.rootURL}/${kbId}/mart/transcripts/touchpoint/${parameters.touchpoint}/start/${parameters.startDate}/end/${parameters.endDate}/match_status/${parameters.matchType}/auth_status/${parameters.authoringStatus}/threshold/${parameters.threshold}/locale/${parameters.localeId}${queryParams}`;
        };
        result.fetch();
      }
      return result;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "learner:transcripts:count",
    function (parameters) {
      const result = new LearnerModel.martTranscriptCount(parameters);
      //only fetch if the start date <= end date
      if (parameters.startDate <= parameters.endDate) {
        result.fetch();
      }
      return result;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "learner:submit:clustering:job",
    (parameters) => {
      const clusteringJob = new LearnerModel.clusteringJob();
      if (parameters.startDate <= parameters.endDate) {
        clusteringJob.setAttributesFromLearnerConfigData(parameters);
        clusteringJob.save();
        return clusteringJob;
      } else {
        throw Error("Invalid date range");
      }
    },
    this,
  );

  /*
   * Headers for the mart transcript sortable list.  Depending on whether the results are clustered and the match type, we return different header lists.
   * We also pass the results collection (most likely not yet synced) in order to determine what conversation attributes to list.
   * Once the collection syncs, we check the conversationAttributes hash in the collection for a list of conversation attributes.  We then
   * add those columns to the header list.  We do this only for non-clustered transcripts, since transcripts in clusters may not have the same attributes.
   */
  Messenger.reqres.setHandler(
    "learner:results:headers",
    function (isClustered, matchType, resultsCollection, localeId) {
      var Header = SortableListModel.headerModel;
      var topicMatchName;
      var topicExampleMatch;
      var topicExampleTranslation;
      //if no-matches, then change the topic matches header name to 'possible topic match' (or something similar reflecting that the topic was a near miss)
      if (matchType === 0) {
        topicMatchName = $.t("learner.headers.nearMiss");
      } else {
        topicMatchName = $.t("learner.headers.topicMatches");
        topicExampleMatch = $.t("learner.headers.topicNativeExampleMatches");
        topicExampleTranslation = $.t("learner.headers.topicExampleTranslation");
      }
      var isEnglish = Messenger.reqres.request("kb:language:is:english", localeId);
      var headerArr = [];
      var categoryFiltersHeader = new Header({
        headerName: $.t("learner.headers.categoryFilters"),
        modelAttribute: "categoryFilters",
        filterable: false,
        sortable: false,
        hideable: true,
        hidden: true,
        width: "15%",
        customHtml: function (model) {
          var categoryFilters = model.get("categoryFilters");
          if (categoryFilters && categoryFilters.length) {
            var html = "<ul>";
            for (var i = 0; i < categoryFilters.length; ++i) {
              var categoryFilter = categoryFilters[i];
              html += "<li>" + _.escape(categoryFilter.category) + ": ";
              for (var j = 0; j < categoryFilter.values.length; ++j) {
                if (j > 0) {
                  html += ", ";
                }
                html += _.escape(categoryFilter.values[j]);
              }
              html += "</li>";
            }
            html += "</ul>";
            return html;
          }
          return "";
        },
      });

      if (isClustered) {
        headerArr.push(new Header({ checkbox: true }));
        if (!isEnglish) {
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.clusteredQuestion"),
              modelAttribute: "userUtterance",
              selectable: true,
              sortable: true,
              filterable: true,
              longTextField: true,
              width: "25%",
            }),
          );
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.translatedQuestion"),
              modelAttribute: "translatedUtterance",
              hideable: true,
              sortable: true,
              filterable: true,
              longTextField: true,
              width: "25%",
            }),
          );
        } else {
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.clusteredQuestion"),
              modelAttribute: "userUtterance",
              selectable: true,
              sortable: true,
              filterable: true,
              longTextField: true,
              width: "50%",
            }),
          );
        }
        headerArr.push(
          new Header({
            headerName: $.t("learner.headers.uniqueQuestionsCount"),
            modelAttribute: "count",
            sortable: true,
            defaultSort: true,
            defaultReversed: true,
            filterable: true,
            numericField: true,
            hideable: true,
            width: "10%",
          }),
        );
        headerArr.push(
          new Header({
            headerName: $.t("learner.headers.totalQuestionsCount"),
            modelAttribute: "totalCount",
            sortable: true,
            defaultSort: true,
            defaultReversed: true,
            filterable: true,
            numericField: true,
            hideable: true,
            width: "10%",
          }),
        );
        headerArr.push(
          new Header({
            headerName: topicMatchName,
            modelAttribute: "topicMatchNames",
            arrayField: true,
            sortable: true,
            filterable: true,
            hideable: true,
            hidden: true,
            width: "30%",
          }),
        );
        headerArr.push(
          new Header({
            headerName: $.t("learner.headers.touchPointName"),
            modelAttribute: "touchPointName",
            sortable: true,
            filterable: true,
            textField: true,
            hideable: true,
            hidden: true,
            width: "10%",
          }),
        );
        headerArr.push(
          new Header({
            headerName: $.t("learner.headers.touchPointId"),
            modelAttribute: "touchPointId",
            sortable: true,
            filterable: true,
            numericField: true,
            hideable: true,
            hidden: true,
            width: "10%",
          }),
        );
        headerArr.push(
          new Header({
            headerName: $.t("learner.headers.localeName"),
            modelAttribute: "localeName",
            sortable: true,
            filterable: true,
            textField: true,
            hideable: true,
            hidden: true,
            width: "10%",
          }),
        );
        headerArr.push(
          new Header({
            headerName: $.t("learner.headers.localeId"),
            modelAttribute: "localeId",
            sortable: true,
            filterable: true,
            numericField: true,
            hideable: true,
            hidden: true,
            width: "10%",
          }),
        );
        return new Backbone.Collection(headerArr);
      } else {
        //add agent name and case id if agent assist is enabled
        var kbModel = Messenger.reqres.request("active:kb:model");
        headerArr.push(new Header({ checkbox: true }));
        if (kbModel.get("agentAssist")) {
          if (!isEnglish) {
            headerArr.push(
              new Header({
                headerName: $.t("learner.headers.question"),
                modelAttribute: "userUtterance",
                selectable: true,
                sortable: true,
                filterable: true,
                longTextField: true,
                width: "20%",
              }),
            );
            headerArr.push(
              new Header({
                headerName: $.t("learner.headers.translatedQuestion"),
                modelAttribute: "translatedUtterance",
                sortable: true,
                hideable: true,
                filterable: true,
                longTextField: true,
                width: "20%",
              }),
            );
          } else {
            headerArr.push(
              new Header({
                headerName: $.t("learner.headers.question"),
                modelAttribute: "userUtterance",
                selectable: true,
                sortable: true,
                filterable: true,
                longTextField: true,
                width: "40%",
              }),
            );
          }

          var topicMatchColumnWidth;
          var agentNameColumnWidth;
          var caseIdColumnWidth;
          var dateColumnWidth;
          if (matchType === 1 || matchType === 2) {
            // example matched is only shown for successfull or suggested match
            topicMatchColumnWidth = "10%";
            agentNameColumnWidth = "10%";
            caseIdColumnWidth = "10%";
            dateColumnWidth = "10%";
          } else {
            topicMatchColumnWidth = "15%";
            agentNameColumnWidth = "15%";
            caseIdColumnWidth = "15%";
            dateColumnWidth = "15%";
          }
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.date"),
              modelAttribute: "time",
              sortable: true,
              dateField: true,
              customHtml: function (model) {
                var time = model.get("time");
                return Messenger.reqres.request("get:formatted:dateTime:long", time);
              },
              hideable: true,
              width: dateColumnWidth,
            }),
          );
          headerArr.push(
            new Header({
              headerName: topicMatchName,
              modelAttribute: "topicMatchNames",
              arrayField: true,
              sortable: true,
              filterable: true,
              hideable: true,
              hidden: true,
              width: topicMatchColumnWidth,
            }),
          );
          if (matchType === 1 || matchType === 2) {
            headerArr.push(
              new Header({
                headerName: topicExampleMatch,
                modelAttribute: "topicMatchNativeExamples",
                arrayField: true,
                sortable: true,
                filterable: true,
                hideable: true,
                hidden: true,
                width: "10%",
              }),
            );
            headerArr.push(
              new Header({
                headerName: topicExampleTranslation,
                modelAttribute: "topicMatchExamples",
                arrayField: true,
                sortable: true,
                filterable: true,
                hideable: true,
                hidden: true,
                width: "10%",
              }),
            );
          }
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.analyticsTag"),
              modelAttribute: "analyticsTag",
              hideable: true,
              hidden: true,
              width: "30%",
            }),
          );
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.agentName"),
              modelAttribute: "agentName",
              sortable: true,
              longTextField: true,
              filterable: true,
              width: agentNameColumnWidth,
              hideable: true,
              hidden: true,
            }),
          );
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.caseId"),
              modelAttribute: "caseId",
              sortable: true,
              longTextField: true,
              filterable: true,
              width: caseIdColumnWidth,
              hideable: true,
              hidden: true,
            }),
          );
          headerArr.push(categoryFiltersHeader);
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.touchPointName"),
              modelAttribute: "touchPointName",
              sortable: true,
              filterable: true,
              textField: true,
              hideable: true,
              hidden: true,
              width: "10%",
            }),
          );
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.touchPointId"),
              modelAttribute: "touchPointId",
              sortable: true,
              filterable: true,
              numericField: true,
              hideable: true,
              hidden: true,
              width: "10%",
            }),
          );
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.localeName"),
              modelAttribute: "localeName",
              sortable: true,
              filterable: true,
              textField: true,
              hideable: true,
              hidden: true,
              width: "10%",
            }),
          );
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.localeId"),
              modelAttribute: "localeId",
              sortable: true,
              filterable: true,
              numericField: true,
              hideable: true,
              hidden: true,
              width: "10%",
            }),
          );
        } else {
          if (!isEnglish) {
            headerArr.push(
              new Header({
                headerName: $.t("learner.headers.question"),
                modelAttribute: "userUtterance",
                selectable: true,
                sortable: true,
                filterable: true,
                longTextField: true,
                width: "20%",
              }),
            );
            headerArr.push(
              new Header({
                headerName: $.t("learner.headers.translatedQuestion"),
                modelAttribute: "translatedUtterance",
                hideable: true,
                sortable: true,
                filterable: true,
                longTextField: true,
                width: "20%",
              }),
            );
          } else {
            headerArr.push(
              new Header({
                headerName: $.t("learner.headers.question"),
                modelAttribute: "userUtterance",
                selectable: true,
                sortable: true,
                filterable: true,
                longTextField: true,
                width: "40%",
              }),
            );
          }
          var topicMatchColumnWidth;
          var dateColumnWidth;
          if (matchType === 1 || matchType === 2) {
            topicMatchColumnWidth = "20%";
            dateColumnWidth = "20%";
          } else {
            topicMatchColumnWidth = "25%";
            dateColumnWidth = "25%";
          }
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.date"),
              modelAttribute: "time",
              sortable: true,
              dateField: true,
              customHtml: function (model) {
                var time = model.get("time");
                return Messenger.reqres.request("get:formatted:dateTime:long", time);
              },
              hideable: true,
              width: dateColumnWidth,
            }),
          );
          headerArr.push(
            new Header({
              headerName: topicMatchName,
              modelAttribute: "topicMatchNames",
              arrayField: true,
              sortable: true,
              filterable: true,
              hideable: true,
              hidden: true,
              width: topicMatchColumnWidth,
            }),
          );
          if (matchType === 1 || matchType === 2) {
            headerArr.push(
              new Header({
                headerName: topicExampleMatch,
                modelAttribute: "topicMatchNativeExamples",
                arrayField: true,
                sortable: true,
                filterable: true,
                hideable: true,
                hidden: true,
                width: "10%",
              }),
            );
            headerArr.push(
              new Header({
                headerName: topicExampleTranslation,
                modelAttribute: "topicMatchExamples",
                arrayField: true,
                sortable: true,
                filterable: true,
                hideable: true,
                hidden: true,
                width: "10%",
              }),
            );
          }
          headerArr.push(categoryFiltersHeader);
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.touchPointName"),
              modelAttribute: "touchPointName",
              sortable: true,
              filterable: true,
              textField: true,
              hideable: true,
              hidden: true,
              width: "10%",
            }),
          );
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.touchPointId"),
              modelAttribute: "touchPointId",
              sortable: true,
              filterable: true,
              numericField: true,
              hideable: true,
              hidden: true,
              width: "10%",
            }),
          );
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.localeName"),
              modelAttribute: "localeName",
              sortable: true,
              filterable: true,
              textField: true,
              hideable: true,
              hidden: true,
              width: "10%",
            }),
          );
          headerArr.push(
            new Header({
              headerName: $.t("learner.headers.localeId"),
              modelAttribute: "localeId",
              sortable: true,
              filterable: true,
              numericField: true,
              hideable: true,
              hidden: true,
              width: "10%",
            }),
          );
        }
        var headerCol = new Backbone.Collection(headerArr);
        //when the results collection syncs, add the conversation attribute headers to headerCol
        resultsCollection.onSynced(function () {
          if (resultsCollection.conversationAttributes) {
            var headersAdded = false;
            _.each(
              _.keys(resultsCollection.conversationAttributes).sort(),
              function (attributeName) {
                var attributeHashName = resultsCollection.conversationAttributes[attributeName];
                //silently push the new headers to the collection and then trigger one "reset" event at the end, so we don't have to handle adding each header to the list individually
                headerCol.push(
                  new Header({
                    headerName: attributeName,
                    modelAttribute: attributeHashName,
                    sortable: true,
                    longTextField: true,
                    filterable: true,
                    hideable: true,
                    hidden: true,
                    width: "15%",
                  }),
                  { silent: true },
                );
                headersAdded = true;
              },
              this,
            );
            if (headersAdded) {
              headerCol.trigger("reset");
            }
          }
        }, this);
        return headerCol;
      }
    },
    this,
  );

  return LearnerModel;
});
